import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { format, formatDistanceToNow } from 'date-fns';
import { ArrowLeft, ExternalLink, Clock, Share2, Twitter, Facebook, Linkedin, Copy, Check, Newspaper, Calendar, Lock, Crown, MessageCircle, Bookmark, BookmarkCheck } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import CommentSection from '@/components/CommentSection';
import PaywallModal from '@/components/PaywallModal';
import SEO from '@/components/SEO';
import { useSubscription } from '@/context/SubscriptionContext';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Enhanced party colors with gradients
const PARTY_CONFIG = {
  labour: { color: '#DC2626', bg: 'bg-red-500', text: 'text-red-600', label: 'Labour' },
  conservative: { color: '#2563EB', bg: 'bg-blue-600', text: 'text-blue-600', label: 'Conservative' },
  libdem: { color: '#F97316', bg: 'bg-orange-500', text: 'text-orange-500', label: 'Liberal Democrats' },
  snp: { color: '#EAB308', bg: 'bg-yellow-500', text: 'text-yellow-600', label: 'SNP' },
  reform: { color: '#06B6D4', bg: 'bg-cyan-500', text: 'text-cyan-600', label: 'Reform UK' },
  green: { color: '#22C55E', bg: 'bg-green-500', text: 'text-green-600', label: 'Green' },
  plaid: { color: '#059669', bg: 'bg-emerald-600', text: 'text-emerald-600', label: 'Plaid Cymru' },
  dup: { color: '#991B1B', bg: 'bg-red-800', text: 'text-red-800', label: 'DUP' },
  sinnfein: { color: '#15803D', bg: 'bg-green-700', text: 'text-green-700', label: 'Sinn Féin' }
};

const getPartyConfig = (party) => PARTY_CONFIG[party] || { color: '#6B7280', bg: 'bg-gray-500', text: 'text-gray-600', label: party };

export default function ArticlePage() {
  const { id } = useParams();
  const { checkArticleAccess, isPremium, getRemainingArticles, FREE_ARTICLES_PER_DAY } = useSubscription();
  const [article, setArticle] = useState(null);
  const [shareData, setShareData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);
  const [showPaywall, setShowPaywall] = useState(false);
  const [hasAccess, setHasAccess] = useState(true);
  const [remaining, setRemaining] = useState(FREE_ARTICLES_PER_DAY);
  const [isBookmarked, setIsBookmarked] = useState(false);

  useEffect(() => {
    fetchArticle();
    // Check if article is bookmarked
    const bookmarks = JSON.parse(localStorage.getItem('watchtower_bookmarks') || '[]');
    setIsBookmarked(bookmarks.includes(id));
  }, [id]);

  // Update page title when article loads
  useEffect(() => {
    if (article) {
      document.title = `${article.title} | The Watchtower`;
    }
    return () => {
      document.title = 'The Watchtower - Holding Power Accountable';
    };
  }, [article]);

  const fetchArticle = async () => {
    try {
      const [articleRes, shareRes] = await Promise.all([
        axios.get(`${API}/articles/${id}`),
        axios.get(`${API}/articles/${id}/share`).catch(() => null)
      ]);
      setArticle(articleRes.data);
      if (shareRes) setShareData(shareRes.data);
      
      // Check access after loading article
      const access = await checkArticleAccess(id);
      setHasAccess(access.canAccess);
      setRemaining(access.remaining);
      
      if (!access.canAccess) {
        setShowPaywall(true);
      }
    } catch (err) {
      setError('Article not found');
    } finally {
      setLoading(false);
    }
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      toast.success('Link copied to clipboard');
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      toast.error('Failed to copy link');
    }
  };

  const handleShareTwitter = () => {
    const text = shareData?.twitter_text || `${article.title} via @TheWatchtowerPo`;
    const url = window.location.href;
    window.open(
      `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`,
      '_blank',
      'width=600,height=400'
    );
  };

  const handleShareFacebook = () => {
    const url = window.location.href;
    window.open(
      `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
      '_blank',
      'width=600,height=400'
    );
  };

  const handleShareLinkedIn = () => {
    const url = window.location.href;
    window.open(
      `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`,
      '_blank',
      'width=600,height=400'
    );
  };

  const handleShareWhatsApp = () => {
    const text = `${article.title} - ${window.location.href}`;
    window.open(
      `https://wa.me/?text=${encodeURIComponent(text)}`,
      '_blank'
    );
  };

  const handleToggleBookmark = () => {
    const bookmarks = JSON.parse(localStorage.getItem('watchtower_bookmarks') || '[]');
    if (isBookmarked) {
      const updated = bookmarks.filter(b => b !== id);
      localStorage.setItem('watchtower_bookmarks', JSON.stringify(updated));
      setIsBookmarked(false);
      toast.success('Removed from reading list');
    } else {
      bookmarks.push(id);
      localStorage.setItem('watchtower_bookmarks', JSON.stringify(bookmarks));
      // Also store article details for offline access
      const bookmarkDetails = JSON.parse(localStorage.getItem('watchtower_bookmark_details') || '{}');
      bookmarkDetails[id] = {
        title: article.title,
        party: article.party,
        source_name: article.source_name,
        published_date: article.published_date
      };
      localStorage.setItem('watchtower_bookmark_details', JSON.stringify(bookmarkDetails));
      setIsBookmarked(true);
      toast.success('Added to reading list');
    }
  };

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid="article-loading">
        <div className="skeleton h-8 w-32 mb-8"></div>
        <div className="skeleton h-12 w-full mb-4"></div>
        <div className="skeleton h-12 w-3/4 mb-8"></div>
        <div className="skeleton h-64 w-full"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-24 text-center" data-testid="article-error">
        <h1 className="font-serif text-3xl mb-4">Article Not Found</h1>
        <p className="text-slate-500 mb-8">The article you're looking for doesn't exist.</p>
        <Link to="/">
          <Button className="btn-primary">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Home
          </Button>
        </Link>
      </div>
    );
  }
  
  return (
    <article className="min-h-screen bg-gradient-to-b from-slate-50 to-white pb-20" data-testid="article-page">
      {/* Dynamic SEO Meta Tags */}
      <SEO 
        title={article.title}
        description={article.summary || article.content?.substring(0, 160)}
        type="article"
        article={article}
      />
      
      {/* Header with Party Color */}
      <header className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Back Link */}
          <Link 
            to="/" 
            className="inline-flex items-center text-sm text-slate-300 hover:text-white mb-6 transition-colors"
            data-testid="back-link"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to News
          </Link>

          {/* Meta Badges */}
          <div className="flex flex-wrap items-center gap-3 mb-6">
            <span 
              className={`text-sm font-bold tracking-wider uppercase px-3 py-1 rounded-full ${getPartyConfig(article.party).bg} text-white`}
            >
              {getPartyConfig(article.party).label}
            </span>
            {article.region && (
              <span className="text-sm text-slate-300 bg-white/10 px-3 py-1 rounded-full">
                {article.region}
              </span>
            )}
            {article.source_name && (
              <span className="text-sm text-slate-300 bg-white/10 px-3 py-1 rounded-full flex items-center gap-1">
                <Newspaper className="h-3 w-3" />
                {article.source_name}
              </span>
            )}
          </div>

          {/* Title */}
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-black leading-tight mb-6" data-testid="article-title">
            {article.title}
          </h1>

          {/* Date info */}
          <div className="flex flex-wrap items-center gap-4 text-sm text-slate-300">
            {article.published_date && (
              <span className="flex items-center gap-1">
                <Calendar className="h-4 w-4" />
                {format(new Date(article.published_date), 'dd MMM yyyy, HH:mm')}
              </span>
            )}
            <span className="flex items-center gap-1">
              <Clock className="h-4 w-4" />
              {formatDistanceToNow(new Date(article.published_date || article.created_at), { addSuffix: true })}
            </span>
          </div>

          {/* Politician */}
          {article.politician_name && (
            <p className="mt-4 text-slate-300">
              Regarding: <strong className="text-white">{article.politician_name}</strong>
            </p>
          )}
        </div>
      </header>

      {/* Action Bar */}
      <div className="bg-white border-b border-slate-200 sticky top-16 z-40">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {article.source_url && (
                <a 
                  href={article.source_url} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-4 py-2 bg-slate-100 hover:bg-slate-200 rounded-lg text-sm font-medium transition-colors"
                  data-testid="view-source"
                >
                  <ExternalLink className="h-4 w-4" />
                  <span>Original Source</span>
                </a>
              )}
            </div>
            
            <div className="flex items-center gap-2">
              {/* Share Dropdown */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="gap-2" data-testid="share-button">
                    <Share2 className="h-4 w-4" />
                    Share
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  <DropdownMenuItem onClick={handleShareTwitter} data-testid="share-twitter">
                    <Twitter className="h-4 w-4 mr-2" />
                    Share on X
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={handleShareFacebook} data-testid="share-facebook">
                    <Facebook className="h-4 w-4 mr-2" />
                    Share on Facebook
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={handleShareLinkedIn} data-testid="share-linkedin">
                    <Linkedin className="h-4 w-4 mr-2" />
                    Share on LinkedIn
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={handleShareWhatsApp} data-testid="share-whatsapp">
                    <MessageCircle className="h-4 w-4 mr-2" />
                    Share on WhatsApp
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={handleCopyLink} data-testid="copy-link">
                    {copied ? <Check className="h-4 w-4 mr-2 text-green-600" /> : <Copy className="h-4 w-4 mr-2" />}
                    {copied ? 'Copied!' : 'Copy Link'}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              
              {/* Bookmark Button */}
              <Button 
                variant={isBookmarked ? "default" : "ghost"}
                className={isBookmarked ? "bg-blue-600 hover:bg-blue-700 text-white" : ""}
                onClick={handleToggleBookmark}
                data-testid="bookmark-button"
              >
                {isBookmarked ? (
                  <>
                    <BookmarkCheck className="h-4 w-4 mr-2" />
                    Saved
                  </>
                ) : (
                  <>
                    <Bookmark className="h-4 w-4 mr-2" />
                    Save
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Remaining articles indicator */}
        {!isPremium && hasAccess && remaining >= 0 && (
          <div className="mb-6 p-4 bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-xl flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Crown className="h-5 w-5 text-amber-600" />
              <span className="text-sm font-medium text-amber-800">
                {remaining === 0 
                  ? "This is your last free article today" 
                  : `${remaining} free article${remaining !== 1 ? 's' : ''} remaining today`}
              </span>
            </div>
            <Link to="/subscribe" className="text-sm font-bold text-amber-600 hover:text-amber-700">
              Go Premium
            </Link>
          </div>
        )}

        <div className={`prose prose-slate prose-lg max-w-none ${!hasAccess ? 'relative' : ''}`} data-testid="article-content">
          {/* Render content with proper paragraph breaks */}
          {hasAccess ? (
            article.content.split('\n\n').map((paragraph, index) => (
              <p key={index} className="text-lg leading-relaxed text-slate-700 mb-6">
                {paragraph}
              </p>
            ))
          ) : (
            <>
              {/* Show first paragraph */}
              <p className="text-lg leading-relaxed text-slate-700 mb-6">
                {article.content.split('\n\n')[0]}
              </p>
              {/* Blurred content */}
              <div className="relative">
                <div className="blur-sm select-none pointer-events-none">
                  {article.content.split('\n\n').slice(1, 4).map((paragraph, index) => (
                    <p key={index} className="text-lg leading-relaxed text-slate-700 mb-6">
                      {paragraph}
                    </p>
                  ))}
                </div>
                {/* Overlay */}
                <div className="absolute inset-0 bg-gradient-to-b from-transparent via-white/80 to-white flex items-end justify-center pb-8">
                  <div className="text-center">
                    <Lock className="h-8 w-8 text-slate-400 mx-auto mb-3" />
                    <p className="text-slate-600 mb-4">Subscribe to read the full article</p>
                    <Button 
                      onClick={() => setShowPaywall(true)}
                      className="bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700"
                    >
                      <Crown className="h-4 w-4 mr-2" /> Unlock Full Article
                    </Button>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Read Full Story CTA */}
        {article.source_url && (
          <div className="mt-8 p-6 bg-slate-100 border border-slate-200 rounded-lg">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div>
                <p className="font-medium text-slate-900">Read the full story</p>
                <p className="text-sm text-slate-500">
                  Continue reading at {article.source_name || 'the original source'} for complete coverage
                </p>
              </div>
              <a 
                href={article.source_url} 
                target="_blank" 
                rel="noopener noreferrer"
                className="btn-primary inline-flex items-center space-x-2 whitespace-nowrap"
              >
                <span>Visit Source</span>
                <ExternalLink className="h-4 w-4" />
              </a>
            </div>
          </div>
        )}

        {/* Share CTA */}
        <div className="mt-8 p-6 bg-slate-900 text-white flex flex-col sm:flex-row items-center justify-between gap-4">
          <div>
            <p className="font-medium">Help spread awareness</p>
            <p className="text-sm text-slate-400">Share this article to hold politicians accountable</p>
          </div>
          <div className="flex items-center space-x-2">
            <Button 
              size="sm" 
              variant="outline" 
              className="bg-transparent border-white text-white hover:bg-white hover:text-black"
              onClick={handleShareTwitter}
            >
              <Twitter className="h-4 w-4" />
            </Button>
            <Button 
              size="sm" 
              variant="outline" 
              className="bg-transparent border-white text-white hover:bg-white hover:text-black"
              onClick={handleShareFacebook}
            >
              <Facebook className="h-4 w-4" />
            </Button>
            <Button 
              size="sm" 
              variant="outline" 
              className="bg-transparent border-white text-white hover:bg-white hover:text-black"
              onClick={handleShareLinkedIn}
            >
              <Linkedin className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Source Info */}
        <div className="mt-8 p-6 bg-slate-50 border border-slate-200">
          <p className="text-xs font-mono text-slate-500 uppercase tracking-wider mb-2">
            Source Information
          </p>
          <p className="text-sm text-slate-600 mb-3">
            This article was {article.source === 'scraped' || article.source === 'google_news' ? 'automatically aggregated' : article.source === 'tip' ? 'submitted via our tip line' : 'manually submitted'} 
            {article.source_name && ` from ${article.source_name}`}. 
            {' '}Content shown is a summary - visit the original source for the full article.
          </p>
          {article.source_url && (
            <a 
              href={article.source_url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm font-medium text-slate-900 hover:underline inline-flex items-center gap-1"
            >
              <ExternalLink className="h-3 w-3" />
              {(() => {
                try {
                  return new URL(article.source_url).hostname.replace('www.', '');
                } catch {
                  return 'View Original Source';
                }
              })()}
            </a>
          )}
        </div>

        {/* Comments */}
        {hasAccess && <CommentSection articleId={article.id} />}
      </div>

      {/* Paywall Modal */}
      <PaywallModal 
        isOpen={showPaywall} 
        onClose={() => setShowPaywall(false)} 
        remaining={remaining}
      />
    </article>
  );
}
